import { apiGet, apiPost, apiRequest, API_URL, ApiError } from "@/lib/api/client";
import { logActivity } from "@/services/gpsdrive-activity-log.service";
import { GpsDriveApiError, gpsDrivePost } from "@/lib/api/gpsdrive-client";
import { isClientSession } from "@/lib/current-account";
import type { Command, CommandType, TraccarCommandType } from "@/types";

// Tipos de comando do app com correspondente real no Traccar
// (POST /api/commands/send). Tipos sem entrada aqui ainda não têm
// integração real — caem no fallback local abaixo.
const TRACCAR_COMMAND_TYPE: Partial<Record<CommandType, string>> = {
  block: "engineStop",
  unblock: "engineResume",
  reboot: "rebootDevice",
  locate: "positionSingle",
};

// Aba LOGS (Editar Usuário) — só os tipos que o pedido original citou
// explicitamente (bloquear/desbloquear) têm entrada dedicada; qualquer
// outro comando mapeado (reboot, locate, ...) cai em VEHICLE_COMMAND
// genérico. Falha já é coberta pelo catch-all de erro em client.ts (não
// duplicado aqui).
const COMMAND_LOG_TYPE: Partial<Record<CommandType, "VEHICLE_BLOCK" | "VEHICLE_UNBLOCK">> = {
  block: "VEHICLE_BLOCK",
  unblock: "VEHICLE_UNBLOCK",
};

// Cliente é limitCommands no Traccar (segurança 2026-09-24): envia pelo GPS
// Drive Backend, que confere devices.lock/unlock/commands.send_command
// antes de mandar com a conta de serviço. Equipe continua direto no Traccar.
// Erro vira ApiError (mesmo tipo do caminho direto) — o fallback por SMS
// (send-block-command-with-sms-fallback.ts) e as telas tratam só ApiError.
async function sendViaBackend(
  deviceId: number,
  body: { type: string; attributes?: Record<string, unknown>; textChannel?: boolean },
): Promise<void> {
  try {
    await gpsDrivePost<unknown>(`/api/v1/vehicles/${deviceId}/commands`, body);
  } catch (err) {
    if (err instanceof GpsDriveApiError) throw new ApiError(err.message, err.status);
    throw err;
  }
}

async function sendToTraccar(vehicleId: string, type: CommandType, traccarType: string): Promise<Command> {
  if (isClientSession()) {
    await sendViaBackend(Number(vehicleId), { type: traccarType });
  } else {
    await apiRequest("/api/commands/send", {
      method: "POST",
      credentials: "include",
      body: { deviceId: Number(vehicleId), type: traccarType },
    });
  }
  logActivity({
    type: COMMAND_LOG_TYPE[type] ?? "VEHICLE_COMMAND",
    message: `Comando "${type}" enviado ao veículo ${vehicleId}`,
    metadata: { vehicleId, commandType: type, traccarType },
  });
  return {
    id: `${vehicleId}-${Date.now()}`,
    vehicleId,
    type,
    status: "sent",
    createdAt: new Date().toISOString(),
  };
}

// Capacidades do servidor Traccar relevantes para o popup de envio de
// comando (GET /api/server). `textEnabled` reflete se o servidor tem um
// gateway de SMS configurado — sem isso, nenhum comando pode sair por SMS,
// não importa o que o dispositivo suporte.
export interface ServerCommandCapabilities {
  textEnabled: boolean;
}

async function getServerCapabilities(): Promise<ServerCommandCapabilities> {
  if (!API_URL) return { textEnabled: false };
  try {
    const server = await apiRequest<{ textEnabled?: boolean }>("/api/server", {
      method: "GET",
      credentials: "include",
    });
    return { textEnabled: !!server.textEnabled };
  } catch {
    return { textEnabled: false };
  }
}

export const commandsService = {
  list: (vehicleId?: string) =>
    apiGet<Command[]>(vehicleId ? `/commands?vehicleId=${vehicleId}` : "/commands", []),
  send: (vehicleId: string, type: CommandType) => {
    const traccarType = TRACCAR_COMMAND_TYPE[type];
    if (API_URL && traccarType) return sendToTraccar(vehicleId, type, traccarType);
    return apiPost<Command>(`/vehicles/${vehicleId}/commands`, { type });
  },

  // Tipos de comando realmente suportados pelo protocolo do dispositivo
  // (GET /api/commands/types?deviceId=) — usado pelo popup "Enviar comando"
  // pra montar a lista dinamicamente. Sem API configurada, não inventa
  // nada: devolve lista vazia.
  types: (deviceId: number): Promise<TraccarCommandType[]> =>
    API_URL
      ? apiRequest<TraccarCommandType[]>(
          `/api/commands/types?${new URLSearchParams({ deviceId: String(deviceId) }).toString()}`,
          { method: "GET", credentials: "include" },
        )
      : Promise.resolve([]),

  getServerCapabilities,

  // Envio genérico (tipo + atributos + canal) usado pelo popup "Enviar
  // comando" — via fetch direto (em vez de apiRequest) pra conseguir ler o
  // corpo de erro real que o Traccar devolve (ex.: comando não suportado
  // pelo protocolo) e mostrar isso pro usuário, em vez de um texto genérico.
  async sendRaw(
    deviceId: number,
    type: string,
    attributes: Record<string, unknown>,
    textChannel: boolean,
  ): Promise<void> {
    if (isClientSession()) {
      try {
        await sendViaBackend(deviceId, { type, attributes, textChannel });
      } catch (err) {
        const message = err instanceof Error && err.message ? err.message : "Erro ao enviar comando";
        logActivity({
          type: "ERROR",
          message,
          metadata: { path: `/api/v1/vehicles/${deviceId}/commands`, method: "POST", deviceId, commandType: type },
        });
        throw err;
      }
      logActivity({
        type: type === "engineStop" ? "VEHICLE_BLOCK" : type === "engineResume" ? "VEHICLE_UNBLOCK" : "VEHICLE_COMMAND",
        message: `Comando "${type}" enviado ao veículo ${deviceId}`,
        metadata: { deviceId, commandType: type, textChannel },
      });
      return;
    }
    const res = await fetch(`${API_URL}/api/commands/send`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ deviceId, type, attributes, textChannel }),
    });
    if (!res.ok) {
      const text = (await res.text().catch(() => "")).trim();
      const message = text || `Erro ${res.status} ao enviar comando`;
      // Vai por fetch direto (não apiRequest), então não passa pelo
      // catch-all de erro em client.ts — precisa logar aqui.
      logActivity({
        type: "ERROR",
        message,
        metadata: { path: "/api/commands/send", method: "POST", status: res.status, deviceId, commandType: type },
      });
      throw new ApiError(message, res.status);
    }
    logActivity({
      type: type === "engineStop" ? "VEHICLE_BLOCK" : type === "engineResume" ? "VEHICLE_UNBLOCK" : "VEHICLE_COMMAND",
      message: `Comando "${type}" enviado ao veículo ${deviceId}`,
      metadata: { deviceId, commandType: type, textChannel },
    });
  },
};
